import { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { SubdomainConfiguration } from './SubdomainConfiguration';
import { CustomDomainConfiguration } from './CustomDomainConfiguration';
import { JSMAssessmentCard } from './JSMAssessmentCard';
import { JSMAssessmentDemo } from './JSMAssessmentDemo';
import { DashboardActionItems } from './DashboardActionItems';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';

/**
 * Edit Company › Setup (RESTORE_PLAN R3.6, carrying the retired
 * CompanyDashboard, which a system admin reached by clicking a company name):
 * the company's subdomain, its custom domain, the JSM assessment (the card,
 * and the assessment itself, the old JSMAssessmentDemo flow), and the
 * company's action items and notifications.
 */
export function CompanyDomainsAndAssessment({ companyId, company, accessToken, currentUser, onUpdated }: {
  companyId: string;
  company: any;
  accessToken: string;
  currentUser?: any;
  onUpdated: () => void;
}) {
  const [firm, setFirm] = useState<any>(null);
  const [assessing, setAssessing] = useState(false);
  const firmId: string | undefined = company?.consultingFirmId;

  useEffect(() => {
    let live = true;
    if (!firmId) return;
    fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms/${firmId}`, { headers: getAuthHeaders(accessToken) })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => { if (live && b) setFirm(b.firm ?? b); })
      .catch(() => {});
    return () => { live = false; };
  }, [firmId, accessToken]);

  if (assessing) {
    return (
      <div className="space-y-4" data-testid="jsm-assessment">
        <Button variant="ghost" size="sm" onClick={() => setAssessing(false)}><ArrowLeft className="size-4 mr-2" aria-hidden="true" />Back to setup</Button>
        <JSMAssessmentDemo
          companyId={companyId}
          companyName={company?.name || ''}
          accessToken={accessToken}
          currentUser={currentUser}
          isConsultant
          onComplete={() => setAssessing(false)}
          onBack={() => setAssessing(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-setup-extras">
      <JSMAssessmentCard
        accessToken={accessToken}
        companyId={companyId}
        companyName={company?.name || ''}
        currentUser={currentUser}
        onStartAssessment={() => setAssessing(true)}
        onViewAssessment={() => setAssessing(true)}
      />
      <SubdomainConfiguration companyId={companyId} company={company || {}} accessToken={accessToken} onUpdate={onUpdated} />
      <CustomDomainConfiguration
        companyId={companyId}
        company={company || {}}
        consultingFirm={firm ? { id: firm.id, name: firm.name, subscription_plan: firm.subscriptionPlan ?? firm.subscription_plan } : undefined}
        accessToken={accessToken}
        onUpdate={onUpdated}
      />
      <DashboardActionItems accessToken={accessToken} currentUser={currentUser} companyId={companyId} />
    </div>
  );
}
